import React, { useCallback, useState } from 'react';
import { Form } from 'antd';
import { useDispatch } from 'react-redux';

import CustomButton from '../../button';
import LinkComponent from '../../linkComponent';
import AuthWrapper from '../authWrapper';
import FormInput from '../../inputWrapper/formInput';
import {
  maxLength,
  minLength,
  requiredField,
  validateEmail,
  validateUserName
} from '../../../helpers/validationHelper';
import { useAuth } from '../../../routes/authContext';
import { setActiveTabKey, setModalOpen } from '../../../redux/slices/modalSlice';
import { useRegisterMutation } from '../../../redux/slices/apiSlice';

import { translations } from '../../../constants';
import { INPUT_TYPES } from '../../../constants/componentTypes';
import { ecclesiaOptions } from '../../../constants/userRoles';

import MainLoader from '../../loader/mainLoader';

const { en } = translations;

const SignUpComponent = ({ onRegisterSuccess }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [register, { isLoading }] = useRegisterMutation();
  const { openNotification } = useAuth();
  const [showEcclesiaField, setShowEcclesiaField] = useState(false);

  const handleEcclesiaChange = (value) => {
    if (value === 2) {
      setShowEcclesiaField(true);
    } else {
      setShowEcclesiaField(false);
      form.setFieldsValue({ homeEcclesiaName: '' });
    }
  };



  const handleSubmit = async (values) => {
    try {
      const response = await register({
        userName: values.username,
        displayName: values.displayName,
        email: values.email,
        requestedUserProfile: values.requestedUserProfile,
        homeEcclesiaName: values.homeEcclesiaName || ''
      });

      if (response.error) {
        openNotification({ message: response.error?.data?.message, title: 'Error', type: 'error' });
      } else {
        openNotification({ message: response?.data?.message, title: 'Success', type: 'success' });
      }
      form.resetFields();
    } catch (error) {
      openNotification({
        message: error?.response?.data?.message || en.error.REGISTRATION_FAILED,
        title: 'Error',
        type: 'error'
      });
    } finally {
      dispatch(setModalOpen(false));
    }
  };

  const handleClearData = useCallback(() => {
    form.resetFields();
    setShowEcclesiaField(false);
  }, [form]);

  return (
    <AuthWrapper
      note={en.NOTE}
      footerLink={
        <LinkComponent
          onClick={() => dispatch(setActiveTabKey('1'))}
          title={
            <p className='cursor-default m-0'>
              {en.ALREADY_HAVE_ACCOUNT}{' '}
              <span className='text-blue-link cursor-pointer'>{en.SIGN_IN}</span>
            </p>
          }
          className='bookroad-signup-tab-footerlink w-100 cursor-pointer manrope-14-regular'
        />
      }
    >
      <div className='signin-modal-container relative'>
        {isLoading && (
          <div className='loading-overlay'>
            <MainLoader style={{ height: 150, width: 150 }} />
          </div>
        )}
        <Form
          form={form}
          name='signUp'
          onFinish={handleSubmit}
          layout='vertical'
          requiredMark={false}
        >
          <FormInput
            label={en.USERNAME_LABEL}
            name='username'
            rules={[requiredField(''), { validator: validateUserName('username', dispatch) }]}
            placeholder={en.USERNAME_PLACEHOLDER}
            onChange={(e) => form.setFieldsValue({ username: e.target.value.trimStart() })}
            tooltipText='Used for authentication purposes.'
          />

          <FormInput
            label={en.DISPLAY_NAME_LABEL}
            name='displayName'
            rules={[
              requiredField(''),
              minLength(3, en.error.MIN_LENGTH_ERROR),
              maxLength(30, en.error.DISPLAYNAME_MAX_LENGTH_ERROR)
            ]}
            placeholder={en.DISPLAY_NAME_PLACEHOLDER}
            onChange={(e) => form.setFieldsValue({ displayName: e.target.value.trimStart() })}
            tooltipText='This name will appear across the system as your visible display name.'
          />

          <FormInput
            label={en.EMAIL_LABEL}
            name='email'
            rules={[requiredField(''), { validator: validateEmail('email', dispatch) }]}
            placeholder={en.EMAIL_PLACEHOLDER}
            onChange={(e) => form.setFieldsValue({ email: e.target.value.trimStart() })}
            tooltipText='Enter a valid email to receive important notifications and messages from the system'
          />

          <FormInput
            type={INPUT_TYPES.SELECT}
            label='Requested User Profile'
            name='requestedUserProfile'
            placeholder='Select Ecclesia'
            value={form.getFieldValue('requestedUserProfile')}
            onChange={handleEcclesiaChange}
            options={ecclesiaOptions}
            rules={[requiredField('Please add your user profile')]}
          />

          {showEcclesiaField && (
            <FormInput
              label='Home Ecclesia Name'
              name='homeEcclesiaName'
              placeholder='Enter Ecclesia Name'
              rules={[requiredField('')]}
            />
          )}

          <CustomButton
            title={en.REGISTER_BUTTON}
            className='mt-16 btn-primary-blue text-white radius-5'
            htmlType='submit'
            loading={isLoading}
            disabled={isLoading}
          />
          {/* <CustomButton
            title={en.CLEAR_BUTTON}
            variant='outline'
            className='mt-16 radius-5'
            onClick={handleClearData}
          /> */}
        </Form>
      </div>
    </AuthWrapper>
  );
};

export default SignUpComponent;
